"use client"

import { useMemo } from "react"
import { Icon } from "@/shared/components/icons"
import { passwordRequirements, getPasswordStrength } from "@/features/auth/validations"
import { cn } from "@/shared/lib/utils"

interface PasswordStrengthIndicatorProps {
  password: string
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strength = useMemo(() => getPasswordStrength(password), [password])
  const metRequirements = useMemo(
    () => passwordRequirements.map((r) => ({ ...r, met: r.regex.test(password) })),
    [password]
  )

  if (!password) return null

  return (
    <div className="space-y-2.5 pt-1">
      {/* Strength bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Password strength</span>
          <span
            className={cn(
              "text-xs font-medium",
              strength.score <= 1 && "text-red-500",
              strength.score === 2 && "text-orange-500",
              strength.score >= 3 && "text-green-500"
            )}
          >
            {strength.label}
          </span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map((level) => (
            <div
              key={level}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors duration-300",
                level <= strength.score ? strength.color : "bg-border"
              )}
            />
          ))}
        </div>
      </div>

      {/* Requirements checklist */}
      <ul className="space-y-1">
        {metRequirements.map((req) => (
          <li key={req.key} className="flex items-center gap-1.5">
            {req.met ? (
              <Icon name="tick" size={12} className="h-3 w-3 text-green-500 shrink-0" />
            ) : (
              <Icon name="x" size={12} className="h-3 w-3 text-muted-foreground/50 shrink-0" />
            )}
            <span
              className={cn(
                "text-xs transition-colors",
                req.met ? "text-green-500" : "text-muted-foreground/60"
              )}
            >
              {req.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
